// Third-party
import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

// Components
import ErrorTooltip from './ErrorTooltip';
import InfoTooltip from './InfoTooltip';
import WarningTooltip from './WarningTooltip';

// Utils
import { DISCLAIMER_LEVEL, DISCLAIMERS } from '../../../utils/disclaimers/constants';
import { formatMessage } from '../../../utils/i18n';
import Disclaimer from '@/types/disclaimer';
import DisclaimerLevel from '@/types/disclaimer-level';

type Props = {
  disclaimer: Disclaimer;
  levelOverride?: DisclaimerLevel;
  showLabel?: boolean;
  className?: string;
  tooltipPlace?: 'bottom' | 'top' | 'left' | 'right';
};

const formatDisclaimerData = (data: any) => {
  if (!data) {
    return '';
  }

  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    if (!value) {
      return acc;
    }

    const newAcc = { ...acc };

    if (Array.isArray(value)) {
      newAcc[key] = value.join(', ');
    } else if (key === 'date') {
      newAcc[key] = moment.utc(value).format(formatMessage('Common.DateFormats.Short'));
    } else {
      newAcc[key] = value;
    }

    return newAcc;
  }, {} as any);
};

const DisclaimerTooltip = ({ disclaimer, levelOverride, showLabel, className, tooltipPlace }: Props) => {
  const { i18nKey, level } = DISCLAIMERS[disclaimer.key];
  const list = disclaimer.data && disclaimer.data.list;

  const title = formatMessage(`Disclaimers.${i18nKey}.Title`).trim();
  const message = formatMessage(`Disclaimers.${i18nKey}.Message`, formatDisclaimerData(disclaimer.data));

  let TooltipComponent;
  switch (levelOverride || level) {
    case DISCLAIMER_LEVEL.WARNING: {
      TooltipComponent = WarningTooltip;
      break;
    }
    case DISCLAIMER_LEVEL.ERROR: {
      TooltipComponent = ErrorTooltip;
      break;
    }
    default: {
      TooltipComponent = InfoTooltip;
      break;
    }
  }

  return (
    <div className={classNames('disclaimer-tooltip', className)}>
      <TooltipComponent place={tooltipPlace} message={message} title={title} list={list} data-private data-dd-privacy="mask" />

      {showLabel && (
        <span data-private data-dd-privacy="mask">
          {title}
        </span>
      )}
    </div>
  );
};

export default DisclaimerTooltip;
